import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GoGeneralInfoTreeNode } from "@/types/go-entity";

interface GoGeneralInfoTreeProps {
  nodes: GoGeneralInfoTreeNode[];
  selectedKlsId: number | null;
  searchValue: string;
  onSelect: (node: GoGeneralInfoTreeNode) => void;
  isLoading?: boolean;
  isError?: boolean;
}

function filterTree(
  nodes: GoGeneralInfoTreeNode[],
  q: string,
): GoGeneralInfoTreeNode[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return nodes;

  const walk = (list: GoGeneralInfoTreeNode[]): GoGeneralInfoTreeNode[] => {
    const out: GoGeneralInfoTreeNode[] = [];
    for (const node of list) {
      const children = walk(node.children ?? []);
      const selfMatch =
        node.label.toLowerCase().includes(needle) ||
        node.code.toLowerCase().includes(needle);
      if (selfMatch || children.length > 0) {
        out.push({ ...node, children });
      }
    }
    return out;
  };
  return walk(nodes);
}

function TreeRow({
  node,
  depth,
  selectedKlsId,
  expanded,
  onToggle,
  onSelect,
}: {
  node: GoGeneralInfoTreeNode;
  depth: number;
  selectedKlsId: number | null;
  expanded: Set<number>;
  onToggle: (klsId: number) => void;
  onSelect: (node: GoGeneralInfoTreeNode) => void;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen = expanded.has(node.kls_id);
  const selected = selectedKlsId === node.kls_id;

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-1 px-2 py-1.5 text-left text-sm hover:bg-sidebar-accent/80",
          selected && "bg-primary/10 dark:bg-primary/15 border-l-4 border-l-primary",
        )}
        style={{ paddingLeft: 8 + depth * 12 }}
        onClick={() => onSelect(node)}
        title={`${node.code} ${node.label}`.trim()}
      >
        {hasChildren ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={isOpen ? "Свернуть" : "Развернуть"}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.kls_id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onToggle(node.kls_id);
              }
            }}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        ) : (
          <span className="inline-block h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate">
          <span className="text-muted-foreground mr-1 tabular-nums">{node.code}</span>
          {node.label}
        </span>
      </button>
      {hasChildren && isOpen
        ? node.children.map((ch) => (
            <TreeRow
              key={ch.kls_id}
              node={ch}
              depth={depth + 1}
              selectedKlsId={selectedKlsId}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))
        : null}
    </div>
  );
}

/**
 * Раскрываемое дерево разделов «Общие сведения» (KLS VGO).
 */
export function GoGeneralInfoTree({
  nodes,
  selectedKlsId,
  searchValue,
  onSelect,
  isLoading = false,
  isError = false,
}: GoGeneralInfoTreeProps) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const filtered = useMemo(() => filterTree(nodes, searchValue), [nodes, searchValue]);

  const onToggle = (klsId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(klsId)) next.delete(klsId);
      else next.add(klsId);
      return next;
    });
  };

  if (isLoading) {
    return <div className="p-3 text-sm text-muted-foreground">Загрузка дерева…</div>;
  }
  if (isError) {
    return (
      <div className="p-3 text-sm text-destructive">
        Не удалось загрузить разделы общих сведений.
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="p-3 text-sm text-muted-foreground">
        {searchValue.trim() ? "Ничего не найдено по фильтру." : "Дерево пусто."}
      </div>
    );
  }

  return (
    <div className="py-1">
      {filtered.map((node) => (
        <TreeRow
          key={node.kls_id}
          node={node}
          depth={0}
          selectedKlsId={selectedKlsId}
          expanded={expanded}
          onToggle={onToggle}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
